import { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import { Box, CssBaseline, Typography, useTheme, useMediaQuery } from "@mui/material";
import { hideAlert } from "../../globalStore/slices/alert/alertSlice";
import { getAssetGroupCount, searchAssets } from "../../utils/API/Assets/Assets";
import {
  setDebouncedValue,
  setResults,
  setSearchResults,
  setSearchedValue,
  setSearchText,
} from "../../globalStore/slices/user/userSlice"
import { useFreshDeskAuth } from "../../app/FreshDesk/useFreshDeskAuth";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import FreshDeskWidget from "../../app/FreshDesk/FreshDeskWidget";
import MaterialConfirmationModal from "../Modals/MaterialConfirmationModal";
import SideNav from "./SideNav/SideNav";
import TopNav from "./TopNav/TopNav";
import { NotificationsProvider } from "../../context/NotificationsContext";

export default function AuthLayout(props) {
  const [state, setState] = useState({
    noOptionsText: "Start Typing to search...",
  })
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const mainContentRef = useRef(null);
  
  // Responsive breakpoints
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  
  // Theme-aware background color
  const backgroundColor = theme.palette.background.default;
  
  const sideNavWidth = 90;
  const appBarHeight = 64;
  
  // Responsive padding based on device
  const getContentPadding = () => {
    if (isDesktop) {
      return {
        paddingLeft: "32px",
        paddingRight: "40px",
        paddingTop: `${appBarHeight + 24}px`,
        paddingBottom: "24px",
      };
    }
    if (isTablet) {
      return {
        paddingLeft: "24px",
        paddingRight: "28px",
        paddingTop: `${appBarHeight + 20}px`,
        paddingBottom: "20px",
      };
    }
    return {
      paddingLeft: "16px",
      paddingRight: "16px",
      paddingTop: `${appBarHeight + 16}px`,
      paddingBottom: "16px",
    };
  };
  
  const classes = {
    root: {
      backgroundColor: backgroundColor,
      display: "flex",
      fontSize: "1rem",
      height: "100vh",
      lineHeight: "1.6",
      overflow: "hidden",
      fontFamily: '"Lato", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    toolbar: {
      // necessary for content to be below app bar
      ...theme.mixins.toolbar,
    },
    content: {
      flexGrow: 1,
      height: "100vh",
      minHeight: "100vh",
      width: `calc(100% - ${sideNavWidth}px)`,
      backgroundColor: backgroundColor,
      transition: "all 0.2s ease-in-out",
      // Trackers (devices) page scrolls internally via the table; no page-level scroll
      overflow: location.pathname === "/devices" ? "hidden" : "auto",
      ...getContentPadding(),
    },
  };
  
  const dispatch = useDispatch();
  const debouncedValue = useSelector((state) => state.user.debouncedValue);
  const alert = useSelector((state) => state.alert);
  const token = useSelector((state) => state.user.token);
  const organization = useSelector((state) => state.organization);
  const { facilities = [], organization: organizationInsideOfRedux = {}, zones = [] } = organization || {};
  const searchText = useSelector((state) => state.user.searchText);
  const searchResults = useSelector((state) => state.user.searchResults);
  const initialResults = useSelector((state) => state.user.initialResults);

  // Its silly but we have a organization.organization inside of our redux store
  const { eventTypesMap = {} } = organizationInsideOfRedux;

  useFreshDeskAuth(token);

  // Scroll main content to top on route change so each page has independent scroll position
  useEffect(() => {
    // Defer so it runs after the new page content is rendered and laid out
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (mainContentRef.current) {
          mainContentRef.current.scrollTop = 0;
        }
        window.scrollTo(0, 0);
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [location.pathname]);

  // Memoize the debounced function so it is not recreated on every render
  const debouncedChange = useMemo(
    () =>
      _.debounce((value) => {

        // If the value length is 0, then the user has cleared the search bar.
        if (value.length === 0) {
          dispatch(setDebouncedValue("")); // Reset debounced value if input is empty
          dispatch(setSearchText(""))
          dispatch(setSearchResults([])); // Clear search results if input is empty

          return;
        }
        dispatch(setDebouncedValue(value)); // Update the debounced value state
      }, 1000),
    [] // Empty dependency array ensures this is only created once
  );

  // This function handles the user typing in the search bar. The useEffect below will handle the query
  function searchBarChange(value) {
    dispatch(setSearchText(value));
    debouncedChange(value); // Trigger debounce for non-empty input
  }

  function onSearchBarClose() {
    // If the value length is 0, we want to clear the search results
    if (searchText.length === 0) {
      dispatch(setSearchResults([]));
    }
  }

  // This function handles the user clicking on a search result
  function onItemClick(event, value) {
    const { label = "", type = "", id = "" } = value || {};
    const currentPath = history.location.pathname;

    // Here we need a if then statement that determins what happens.
    // As the value can be a facility, zone, or asset. If the value is null this means the user has cleared the search bar.
    if (value === null) {
      debouncedChange(""); // Use the debouncedChange function to reset debouncedValue
      dispatch(setResults(initialResults));
      dispatch(setSearchResults([]));
      dispatch(setSearchText(""))
      dispatch(setSearchedValue({}));

      return;
    }
    else if (type === "Assets") {
      history.push(`/assetSnapshot/${id}`);
    } else {
      // We need to store this value in redux
      dispatch(setSearchedValue(value));
      dispatch(setSearchText(label))
    }

    if (currentPath !== "/") {
      history.push("/");
    }
  }

  // Update useEffect to listen to debouncedValue and searches off of that value
  useEffect(() => {
    if (debouncedValue) {

      // Search facilities
      const filteredFacilities = Object.values(facilities).filter(facility =>
        typeof facility.name === "string" &&
        facility.name.toLowerCase().includes(debouncedValue.toLowerCase())
      ).map(facility => ({
        ...facility,
        type: "Facility"
      }));

      // Search Zones
      const filteredZones = Object.values(zones).filter(zone =>
        typeof zone.name === "string" &&
        zone.name.toLowerCase().includes(debouncedValue.toLowerCase())
      ).map(zone => ({
        ...zone,
        type: "Zone"
      }));

      // Search Events
      const filteredEvents = Object.values(eventTypesMap)
        .filter(event =>
          typeof event.name === "string" &&
          event.name.toLowerCase().includes(debouncedValue.toLowerCase())
        )
        .map(event => ({
          ...event,
          type: "Event"
        }));

      // Get grouped search results
      const groupSuggestionsPromise = getAssetGroupCount({ tag: debouncedValue, column: "tag", token }).then((res) => {
        return Object.keys(res?.count?.tag_agg || {})
          .filter(key => key.toLowerCase().includes(debouncedValue.toLowerCase()))
          .map(key => ({
            name: key,
            type: "Suggestion",
            value: key,
          }));
      });

      // Search Assets - If an asset is not entered, do we need to run this?
      searchAssets(props, {
        filters: {
          // The debounced Value can be a facility or event name, yes. Not only does this function not know this
          //  since the value comes in as a string. But it should also not stop the user from searching for assets that might have that value.
          tag: debouncedValue,
        },
      }).then(async (res) => {
        const { assets = [], success = false } = res || {};
        const filteredAssets = success
          ? assets.map(asset => ({
            ...asset,
            type: "Assets"
          }))
          : [];
        // Await the group suggestions
        const groupSuggestions = await groupSuggestionsPromise;

        dispatch(setSearchResults([
          ...(groupSuggestions.length ? groupSuggestions : []),
          ...(filteredAssets.length ? filteredAssets : []),
          ...(filteredEvents.length ? filteredEvents : []),
          ...(filteredFacilities.length ? filteredFacilities : []),
          ...(filteredZones.length ? filteredZones : []),
        ]));

        // This if else statement controls the no option text on the dropdown select bar for the topNav
        if (assets.length > 0 && searchText.length === 0) {
          setState({ noOptionsText: "Start Typing to search..." })
        } else if (assets.length === 0 && searchText.length > 0) {
          setState({ noOptionsText: "No assets found" });
        } else {
          // The above else if can probably be the else. But i figured it might be a little more declarative to put the usecase in there ;)
        }

      });
    } else if (debouncedValue === "") {
      setState({ noOptionsText: "Start Typing to search..." })
    }
  }, [debouncedValue]);

  return (
    <>
      <CssBaseline />
      <NotificationsProvider>
        <Box sx={classes.root}>
          <FreshDeskWidget />

          {/* SideNav */}
          <SideNav sideNavWidth={sideNavWidth} appBarHeight={appBarHeight} />

          {/* TopNav */}
          <TopNav
          {...props}
          noOptionsText={state.noOptionsText}
          onItemClick={onItemClick}
          onSearchBarChange={searchBarChange}
          onSearchBarClose={onSearchBarClose}
          searchResults={searchResults}
          searchText={searchText}
          useAssetSearch={false}
          useCategorizedSelect={true}
          useTextBox={false}
        />

        {/* Main Content */}
        <Box ref={mainContentRef} component="main" sx={classes.content}>
          <MaterialConfirmationModal
            closeModal={() => {
              dispatch(hideAlert({}));
            }}
            content={alert.text}
            modalOpen={alert.show}
            severity={alert.type || "error"}
          />
          {props.children ? props.children : null}
          {/* Bottom padding for help desk widget */}
          <Box sx={{ paddingBottom: "80px" }} />
        </Box>
      </Box>
      </NotificationsProvider>
    </>
  );
}
